import { defineNuxtPlugin } from '#app';
import { nextTick, watch } from 'vue';
import { useRoute } from '#imports';
import { logger } from 'data-layer/exports';
import { scrollTo } from '@/assets/javascripts/scroll';

/**
 * Nuxt plugin to handle scrolling to elements based on the URL hash,
 * while accounting for a floating/sticky header.
 */
export default defineNuxtPlugin((nuxtApp) => {
  const route = useRoute();

  // Tweak this selector to reliably match your sticky header(s)
  const HEADER_SELECTOR = '.headerFixed';

  const getStickyHeaderHeight = (): number => {
    const header = document.querySelector<HTMLElement>(HEADER_SELECTOR);
    return header ? header.offsetHeight : 0;
  };

  const updateStickyOffsetVars = (): number => {
    const h = getStickyHeaderHeight();
    const extra = 8; // breathing room
    const total = h + extra;
    document.documentElement.style.setProperty('--sticky-offset', `24px`);
    // Assist native anchor scrolling as well
    document.documentElement.style.scrollPaddingTop = `24px`;
    return total;
  };

  const findTargetByHash = (hash: string): HTMLElement | null => {
    if (!hash || hash === '#') return null;
    const raw = decodeURIComponent(hash.startsWith('#') ? hash.slice(1) : hash);

    let el = document.getElementById(raw);
    if (el) return el;

    if (
      (window as any).CSS &&
      typeof (window as any).CSS.escape === 'function'
    ) {
      const esc = (window as any).CSS.escape(raw);
      el =
        document.querySelector<HTMLElement>(`#${esc}`) ||
        document.querySelector<HTMLElement>(`[name="${esc}"]`);
      if (el) return el;
    }

    const safeAttr = raw.replace(/"/g, '\\"');
    el =
      document.querySelector<HTMLElement>(`[id="${safeAttr}"]`) ||
      document.querySelector<HTMLElement>(`[name="${safeAttr}"]`);
    return el || null;
  };

  const waitForElementAndScroll = async (hash: string) => {
    if (!hash) return;

    let retries = 20;
    while (retries > 0) {
      await nextTick();

      let element: HTMLElement | null = null;
      try {
        element = findTargetByHash(hash);
      } catch {
        element = null;
      }

      if (element) {
        const headerOffset = updateStickyOffsetVars();
        // Use your helper; it will prefer native scrollIntoView (so scroll-margin-top applies)
        scrollTo(element, {
          headerOffset,
          easing: 'smooth',
        });
        return;
      }

      retries -= 1;
      await new Promise((resolve) => setTimeout(resolve, 200));
    }

    logger.warn(`Element with hash "${hash}" not found after waiting.`);
  };

  const scrollToHash = async () => {
    if (route.hash) {
      await waitForElementAndScroll(route.hash);
    }
  };

  const handleResize = () => {
    updateStickyOffsetVars();
  };

  nuxtApp.hook('app:mounted', () => {
    updateStickyOffsetVars();
    window.addEventListener('resize', handleResize, { passive: true });
  });

  nuxtApp.hook('page:finish', async () => {
    updateStickyOffsetVars();
    await scrollToHash();
  });

  watch(
    () => route.hash,
    async (newHash) => {
      if (newHash) {
        updateStickyOffsetVars();
        await scrollToHash();
      }
    },
    { immediate: true },
  );
});
